import { 
  LabelDefinition, 
  ApiKeyMetadata, 
  BudgetTracking,
  LabelDefinitionsStorage,
  ApiKeyMetadataStorage,
  BudgetTrackingStorage,
  STORAGE_KEYS
} from '@/types/label-budget';
import { debugLog } from './debug-log';

const CURRENT_VERSION = '1.0';

/**
 * Label Definitions Management
 */
export const saveLabelDefinitions = (definitions: LabelDefinition[]): boolean => {
  try {
    const data: LabelDefinitionsStorage = {
      version: CURRENT_VERSION,
      definitions,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEYS.LABEL_DEFINITIONS, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Failed to save label definitions:', error);
    return false;
  }
};

export const getLabelDefinitions = (): LabelDefinition[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.LABEL_DEFINITIONS);
    if (!stored) return [];

    const data: LabelDefinitionsStorage = JSON.parse(stored);
    
    // Version check and migration if needed
    if (data.version !== CURRENT_VERSION) {
      console.warn('Label definitions version mismatch, may need migration');
    }

    return data.definitions || [];
  } catch (error) {
    console.error('Failed to get label definitions:', error);
    return [];
  }
};

export const removeLabelDefinitions = (): boolean => {
  try {
    localStorage.removeItem(STORAGE_KEYS.LABEL_DEFINITIONS);
    return true;
  } catch (error) {
    console.error('Failed to remove label definitions:', error);
    return false;
  }
};

/**
 * API Key Metadata Management
 */
export const saveApiKeyMetadata = (metadata: ApiKeyMetadata[]): boolean => {
  try {
    const data: ApiKeyMetadataStorage = {
      version: CURRENT_VERSION,
      metadata,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEYS.API_KEY_METADATA, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Failed to save API key metadata:', error);
    return false;
  }
};

export const getApiKeyMetadata = (): ApiKeyMetadata[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.API_KEY_METADATA);
    if (!stored) return [];

    const data: ApiKeyMetadataStorage = JSON.parse(stored);
    
    // Version check and migration if needed
    if (data.version !== CURRENT_VERSION) {
      console.warn('API key metadata version mismatch, may need migration');
    }

    return data.metadata || [];
  } catch (error) {
    console.error('Failed to get API key metadata:', error);
    return [];
  }
};

export const getApiKeyMetadataById = (apiKeyId: string): ApiKeyMetadata | null => {
  const metadata = getApiKeyMetadata();
  return metadata.find(m => m.apiKeyId === apiKeyId) || null;
};

export const updateApiKeyMetadata = (apiKeyId: string, updates: Partial<ApiKeyMetadata>): boolean => {
  try {
    const metadata = getApiKeyMetadata();
    const index = metadata.findIndex(m => m.apiKeyId === apiKeyId);
    
    if (index === -1) {
      // Create new metadata if not exists
      const newMetadata: ApiKeyMetadata = {
        apiKeyId,
        apiKeyName: updates.apiKeyName || '',
        labels: updates.labels || {},
        ...updates,
        updatedAt: new Date().toISOString()
      };
      metadata.push(newMetadata);
    } else {
      // Update existing metadata
      metadata[index] = {
        ...metadata[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };
    }

    return saveApiKeyMetadata(metadata);
  } catch (error) {
    console.error('Failed to update API key metadata:', error);
    return false;
  }
};

export const removeApiKeyMetadata = (apiKeyId: string): boolean => {
  try {
    const metadata = getApiKeyMetadata();
    const filtered = metadata.filter(m => m.apiKeyId !== apiKeyId);
    return saveApiKeyMetadata(filtered);
  } catch (error) {
    console.error('Failed to remove API key metadata:', error);
    return false;
  }
};

/**
 * Budget Tracking Management
 */
export const saveBudgetTracking = (tracking: BudgetTracking[]): boolean => {
  try {
    const data: BudgetTrackingStorage = {
      version: CURRENT_VERSION,
      tracking,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEYS.BUDGET_TRACKING, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Failed to save budget tracking:', error);
    return false;
  }
};

export const getBudgetTracking = (): BudgetTracking[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.BUDGET_TRACKING);
    if (!stored) return [];

    const data: BudgetTrackingStorage = JSON.parse(stored);
    
    // Version check and migration if needed
    if (data.version !== CURRENT_VERSION) {
      console.warn('Budget tracking version mismatch, may need migration');
    }

    return data.tracking || [];
  } catch (error) {
    console.error('Failed to get budget tracking:', error);
    return [];
  }
};

export const getBudgetTrackingByKey = (apiKeyId: string, period: string): BudgetTracking | null => {
  const tracking = getBudgetTracking();
  return tracking.find(t => t.apiKeyId === apiKeyId && t.period === period) || null;
};

export const updateBudgetTracking = (apiKeyId: string, period: string, updates: Partial<BudgetTracking>): boolean => {
  try {
    const tracking = getBudgetTracking();
    const index = tracking.findIndex(t => t.apiKeyId === apiKeyId && t.period === period);
    
    if (index === -1) {
      // Create new tracking if not exists
      const newTracking: BudgetTracking = {
        apiKeyId,
        period,
        usage: 0,
        limit: 0,
        percentage: 0,
        projectedUsage: 0,
        lastCheckedAt: new Date().toISOString(),
        actions: { warningsSent: [] },
        ...updates
      };
      tracking.push(newTracking);
    } else {
      // Update existing tracking
      tracking[index] = {
        ...tracking[index],
        ...updates,
        lastCheckedAt: new Date().toISOString()
      };
    }

    return saveBudgetTracking(tracking);
  } catch (error) {
    console.error('Failed to update budget tracking:', error);
    return false;
  }
};

/**
 * Cleanup and synchronization utilities
 */
export const cleanupOrphanedMetadata = (activeApiKeyIds: string[]): void => {
  try {
    // Clean up metadata for API keys that no longer exist
    const metadata = getApiKeyMetadata();
    const activeSet = new Set(activeApiKeyIds);
    const cleaned = metadata.filter(m => activeSet.has(m.apiKeyId));
    
    if (cleaned.length < metadata.length) {
      debugLog(`Cleaned up ${metadata.length - cleaned.length} orphaned metadata entries`);
      saveApiKeyMetadata(cleaned);
    }

    // Clean up budget tracking for removed API keys
    const tracking = getBudgetTracking();
    const cleanedTracking = tracking.filter(t => activeSet.has(t.apiKeyId));
    
    if (cleanedTracking.length < tracking.length) {
      debugLog(`Cleaned up ${tracking.length - cleanedTracking.length} orphaned tracking entries`);
      saveBudgetTracking(cleanedTracking);
    }
  } catch (error) {
    console.error('Failed to cleanup orphaned metadata:', error);
  }
};

/**
 * Initialize default metadata for new API keys
 */
export const initializeNewApiKeyMetadata = (apiKeyId: string, apiKeyName: string): boolean => {
  const existing = getApiKeyMetadataById(apiKeyId);
  if (existing) return true; // Already exists

  return updateApiKeyMetadata(apiKeyId, {
    apiKeyName,
    labels: {},
    status: 'active'
  });
};

/**
 * Get current period string based on period type
 */
export const getCurrentPeriod = (periodType: 'monthly' | 'daily' | 'weekly'): string => {
  const now = new Date();
  switch (periodType) {
    case 'monthly':
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    case 'weekly': {
      // Get ISO week number
      const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
      const days = Math.floor((now.getTime() - firstDayOfYear.getTime()) / (24 * 60 * 60 * 1000));
      const weekNumber = Math.ceil((days + firstDayOfYear.getDay() + 1) / 7);
      return `${now.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
    }
    case 'daily':
      return now.toISOString().split('T')[0];
    default:
      return now.toISOString().split('T')[0];
  }
};

/**
 * Clear all label and budget data (for testing or reset)
 */
export const clearAllLabelBudgetData = (): boolean => {
  try {
    localStorage.removeItem(STORAGE_KEYS.LABEL_DEFINITIONS);
    localStorage.removeItem(STORAGE_KEYS.API_KEY_METADATA);
    localStorage.removeItem(STORAGE_KEYS.BUDGET_TRACKING);
    return true;
  } catch (error) {
    console.error('Failed to clear label and budget data:', error);
    return false;
  }
};